import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import moment from 'moment';
import { fromEvent, Subscription } from "rxjs";

type Key = string;

@Component({
  selector: 'app-item-dynamic-form',
  imports: [ 
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TableModule,
    CheckboxModule,
    SelectModule,
    DatePickerModule,
    ButtonModule,
    AutoCompleteModule,
    TagModule,
    TooltipModule,
  ],
  standalone: true,
  templateUrl: './item-dynamic-form.html',
  styleUrl: './item-dynamic-form.css',
})
export class ItemDynamicForm implements OnInit, OnChanges {
  
  constructor(
    private fb: FormBuilder, 
    private cdr: ChangeDetectorRef, 
    private sanitizer: DomSanitizer,
    private router: Router
  ) {}
  
  @Input() data: any = {};
  @Input() subType: string = '';
  @Input() formGroup: FormGroup = new FormGroup({});
  @Input() tableKey: string = 'tableRows';
  @Input() tableArray!: FormArray;
  @Input() viewMode: boolean = false;
  @Input() suggestionsMap: Map<string, any[]> = new Map(); // Map with key: "rowIndex_fieldName", value: suggestions[] (for search fields)
  @Input() optionsMap: Map<string, any[]> = new Map(); // Map with key: "rowIndex_fieldName", value: options[] (for select/radio fields)
  @Input() showAddRemoveButtons: boolean = true; // Flag to show/hide plus and cross buttons
  @Input() showAddButton: boolean = true; // Flag to show/hide add button (only applies when showAddRemoveButtons is true)
  @Input() showSerialNumber: boolean = false; // Flag to show/hide serial number column based on rowIndex
  @Input() showSelectionCheckbox: boolean = false; // Flag to show/hide selection checkbox column
  @Input() redirectQueryParamsResolver?: (field: any, control: FormGroup | null | undefined) => Record<string, any>;
  @Input() showBulkActionButtons?: boolean = false; // Flag to show/hide bulk action buttons
  @Input() nonEditableFields: string[] = []; // Fields that should be displayed as read-only with display values
  @Input() regexValidationErrors: { [key: string]: string } = {};
  @Input() shouldHideField?: (field: any, rowControl?: FormGroup, rowIndex?: number) => boolean; // Function to determine if a field should be hidden
  @Input() totalFields: string[] = []; // Array of field names for which totals should be displayed
  @Input() showTotalsRow: boolean | null = null; // Explicit flag to control whether totals row should be displayed
  @Input() customActionColumn?: (rowIndex: number, rowControl: FormGroup) => { show: boolean; label?: string; onClick?: () => void; disabled?: boolean }; // Function to render custom action column content
  @Input() shouldHideDeleteButton?: (rowControl: FormGroup) => boolean; // Function to determine if delete button should be hidden for a specific row
  @Input() showDuplicateRowButton: boolean = false; // Flag to show/hide duplicate/split button per row
  @Input() shouldHideDuplicateButton?: (rowControl: FormGroup) => boolean; // Function to determine if duplicate button should be hidden for a specific row
  @Input() tableActions?: Array<{
    label: string;
    icon?: string;
    svgIcon?: string;
    onClick: () => void;
    disabled?: boolean | (() => boolean);
    visible?: boolean | (() => boolean);
    styleClass?: string;
  }>; // Array of table-level actions to display in table header
  @Input() showPagination: boolean = false;
  @Input() paginationLoading: boolean = false;
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() conversionRate: number | null = null;
  @Input() billInUsd: boolean = false;
  @Input() lineOfBusinessMap: any;
  @Input() masterData: any;
  @Input() colorDotMap: Map<string, string> = new Map(); // Map with key: "rowIndex_fieldName", value: hex color string

  @Output() pageChange = new EventEmitter<number>();

  @Input() extraColumns?: Array<{
    header: string;
    width?: string;
    type: 'text' | 'tag' | 'actions';
    getValue?: (rowIndex: number, rowControl: FormGroup) => string;
    getTagSeverity?: (rowIndex: number, rowControl: FormGroup) => string;
    getActions?: (rowIndex: number, rowControl: FormGroup) => Array<{
      icon: string;
      tooltip: string;
      severity: string;
      visible: boolean;
      onClick: () => void;
    }>;
  }>; // Extra columns appended after dynamic fields (e.g., fulfillment status/actions)

  @Output() fieldChanged = new EventEmitter<{
    key: string;
    value: any;
    fieldName?: string;
    fieldValue?: any;
    autoCleared?: boolean;
    reason?: string;
    rowIndex?: number;
    rowId?: any;
  }>();

  @Output() searchQuery = new EventEmitter<{
    fieldName: string;
    query: string;
    rowIndex: number;
    rowId?: any;
    rowGroup?: FormGroup | any;
  }>();

  @Output() selectionChanged = new EventEmitter<{
    selectedItems: any[];
    selectedIndices: number[];
  }>();

  @Output() fileUploadRequested = new EventEmitter<{
    file: File;
    field: any;
    rowIndex: number;
    rowId?: any;
  }>();

  @Output() fileDownloadRequested = new EventEmitter<{
    s3Key: string;
    fieldName: string;
    field: any;
    rowIndex: number;
    rowId?: any;
  }>();

  @Output() rowAdded = new EventEmitter<{
    rowIndex: number;
    rowControl: FormGroup;
    tableKey: string;
  }>();

  @Output() rowDuplicated = new EventEmitter<{
    sourceRowIndex: number;
    newRowIndex: number;
    newRowControl: FormGroup;
    tableKey: string;
  }>();

  dynamicFormFields: any[] = [];
  private cachedVisibleRows: { control: any; index: number }[] = [];
  private lastFormArrayLength: number = 0;
  // Cache for extra column actions to prevent *ngFor DOM recreation on every CD cycle
  private extraColumnActionsCache = new Map<string, any[]>();
  // Object to store currently selected row index for each radio field
  // Key: fieldName, Value: rowIndex (actual index in form array)
  private radioSelectedRowMap: any = {};

  filteredMap = new Map<Key, HTMLElement[]>();
  activeKey?: Key;
  enterSub?: Subscription;

  getCustomActionConfig(rowIndex: number, rowControl: FormGroup): { show: boolean; label?: string; svgIcon?: string; onClick?: (event?: Event) => void; disabled?: boolean } | null {
    if (!this.customActionColumn) return null;
    try {
      return this.customActionColumn(rowIndex, rowControl);
    } catch (error) {
      console.error('Error getting custom action config:', error);
      return null;
    }
  }

  getSanitizedSvg(svgString: string | undefined): SafeHtml | null {
    if (!svgString) {
      return null;
    }
    // Use bypassSecurityTrustHtml for SVG content as it's trusted content
    return this.sanitizer.bypassSecurityTrustHtml(svgString);
  }

  /**
   * Get cached extra column actions for a row to prevent *ngFor from recreating DOM elements.
   * Returns the same array reference if actions haven't changed, preventing click event loss.
   */
  getExtraColumnActions(colIndex: number, rowIndex: number, rowControl: FormGroup): any[] {
    if (!this.extraColumns) return [];
    const col = this.extraColumns[colIndex];
    if (!col?.getActions) return [];

    const key = `${colIndex}_${rowIndex}`;
    const actions = col.getActions(rowIndex, rowControl);
    const cached = this.extraColumnActionsCache.get(key);

    // Reuse cached reference if action count and visibility haven't changed
    if (cached && cached.length === actions.length) {
      let same = true;
      for (let i = 0; i < actions.length; i++) {
        if (cached[i].icon !== actions[i].icon || cached[i].visible !== actions[i].visible) {
          same = false;
          break;
        }
      }
      if (same) {
        // Update onClick references in cached objects (closures may change)
        for (let i = 0; i < actions.length; i++) {
          cached[i].onClick = actions[i].onClick;
          cached[i].tooltip = actions[i].tooltip;
          cached[i].severity = actions[i].severity;
        }
        return cached;
      }
    }

    this.extraColumnActionsCache.set(key, actions);
    return actions;
  }

  trackByIndex(index: number): number {
    return index;
  }

  handleCustomActionClick(event: Event, actionConfig: { onClick?: (event?: Event) => void }): void {
    event.stopPropagation();
    event.preventDefault();
    if (actionConfig.onClick) {
      actionConfig.onClick(event);
    }
  }

  /**
   * Handle wheel event for horizontal scrolling on trackpad
   * Translates trackpad horizontal gesture (deltaX) to scroll
   * @param event WheelEvent from trackpad/mouse
   * @param container The scrollable container element
   */
  onTableWheel(event: WheelEvent, container: HTMLElement): void {
    // Find PrimeNG's internal table container
    const scrollTarget = container.querySelector('.p-datatable-table-container') as HTMLElement;
    if (!scrollTarget) return;

    const maxScroll = scrollTarget.scrollWidth - scrollTarget.clientWidth;
    if (maxScroll <= 0) return; // Nothing to scroll

    // Only intercept when horizontal scroll is dominant (prevents blocking vertical page scroll on trackpads)
    if (Math.abs(event.deltaX) > 0 && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      const canScrollLeft = scrollTarget.scrollLeft > 0;
      const canScrollRight = scrollTarget.scrollLeft < maxScroll;

      // Only prevent default and scroll if we can scroll in the direction of the gesture
      if ((event.deltaX < 0 && canScrollLeft) || (event.deltaX > 0 && canScrollRight)) {
        event.preventDefault();
        event.stopPropagation();
        scrollTarget.scrollLeft += event.deltaX;
      }
    }
    // If only vertical scroll (deltaY) and shift is held, convert to horizontal
    else if (event.shiftKey && Math.abs(event.deltaY) > 0) {
      event.preventDefault();
      event.stopPropagation();
      scrollTarget.scrollLeft += event.deltaY;
    }
  }

  /**
   * Check if a table action should be visible
   * @param action The table action configuration
   * @returns true if the action should be visible
   */
  isTableActionVisible(action: any): boolean {
    if (typeof action.visible === 'function') {
      return action.visible();
    }
    return action.visible !== false;
  }

  /**
   * Check if a table action should be disabled
   * @param action The table action configuration
   * @returns true if the action should be disabled
   */
  isTableActionDisabled(action: any): boolean {
    if (typeof action.disabled === 'function') {
      return action.disabled();
    }
    return action.disabled === true;
  }

  ngOnInit() {
    this.initializeData();
    this.initializeTable();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['masterData'] && changes['masterData'].currentValue) {
      this.applyMasterOptionsToFields();
    }
    if (changes['data']) {
      if (changes['data'].currentValue) {
        this.initializeData();
        this.initializeTable();
      }
    }
    if (changes['tableArray']) {
      if (changes['tableArray'].currentValue) {
        // Ensure tableArray is connected to formGroup
        this.ensureTableArrayConnected();
        // Only initialize if we have data fields
        if (this.dynamicFormFields.length > 0) {
          this.initializeTable();
        }
        // Invalidate cache to force recalculation of visible rows
        this.cachedVisibleRows = [];
        this.lastFormArrayLength = 0;
      }
    }
    if (changes['formGroup'] && changes['formGroup'].currentValue) {
      // When formGroup changes, ensure tableArray is connected
      this.ensureTableArrayConnected();
      // Only initialize if we have data fields
      if (this.dynamicFormFields.length > 0) {
        this.initializeTable();
      }
    }
    if (changes['suggestionsMap'] && changes['suggestionsMap'].currentValue) {
    }
    if (changes['optionsMap'] && changes['optionsMap'].currentValue) {
      // When optionsMap changes, repopulate optionsMap for all rows
      this.populateOptionsMapForAllRows();
      // Normalize select and radio field values after options are populated
      this.normalizeAllSelectFieldValues();
      this.normalizeRadioButtonValues();
    }
    if (changes['tableArray'] && changes['tableArray'].currentValue) {
      // When table array changes (e.g., rows added/removed), populate optionsMap for new rows
      this.populateOptionsMapForAllRows();
      // Normalize radio button values when table array changes
      this.normalizeRadioButtonValues();
      // Reinitialize radio selected row map when table array changes
      this.initializeRadioSelectedRowMap();
    }
  }
  
  private initializeData() {
    if (Array.isArray(this.data)) {
      this.dynamicFormFields = this.data;
    } else {
      this.dynamicFormFields = [];
    }
    this.applyMasterOptionsToFields();
  }
  
  private applyMasterOptionsToFields() {
    if (!this.masterData) return;

    this.data.forEach((field: any) => {
      if (field.options_master) {
        
        const key = field.options_master;
        const options = this.masterData[key];

        if (Array.isArray(options)) {
          field.options = options;
        } else {
          console.warn(" No options found for:", key);
        }
      }
    });
  }

  private ensureTableArrayConnected() {
    // If tableArray is provided but not connected to formGroup, connect it
    if (this.tableArray && this.formGroup && !this.formGroup.get(this.tableKey)) {
      this.formGroup.addControl(this.tableKey, this.tableArray);
    }
  }
  
  private initializeTable() {
    // Only initialize if we have data fields
    if (this.dynamicFormFields.length === 0) {
      return;
    }
    
    // Ensure tableArray is connected to formGroup first
    this.ensureTableArrayConnected();
    
    const tableArray = this.getTableRowsArray();
    // if (tableArray.length === 0 && !this.viewMode) {
    //   // this.addTableRow();
    // }
    
    // Populate optionsMap for all existing rows
    this.populateOptionsMapForAllRows();
    // Normalize select field values after options are populated
    this.normalizeAllSelectFieldValues();
    // Initialize radio selected row map by checking which rows have true values
    this.initializeRadioSelectedRowMap();
    
    // Apply validators to all existing rows to ensure consistency
    this.applyValidatorsToExistingRows();
  }

  /**
   * Apply validators to all existing rows
   * This ensures that rows created by parent components or loaded from data have the correct validators
   */
  private applyValidatorsToExistingRows(): void {
    const tableArray = this.getTableRowsArray();
    if (!tableArray || tableArray.length === 0) {
      return;
    }

    tableArray.controls.forEach((rowControl) => {
      if (rowControl instanceof FormGroup) {
        this.applyValidatorsForRow(rowControl, { updateValidity: false });
      }
    });
  }

  // Row wise validation function.
  private applyValidatorsForRow(
    rowControl: FormGroup,
    options: { updateValidity?: boolean } = {}
  ): void {
    const { updateValidity = true } = options;
    const isSelected = rowControl.get('_selected')?.value;
    const skipValidation = this.showSelectionCheckbox && !isSelected;

    this.dynamicFormFields.forEach((field: any) => {
      const key = this.getFieldKey(field.field_name);
      const control = rowControl.get(key);
      if (!control) {
        return;
      }
      // Override any existing validators with our comprehensive set (or none).
      control.setValidators(skipValidation ? null : this.getValidatorsForField(field));
    });
  }

  /**
   * Normalize all select field values across all rows
   */
  private normalizeAllSelectFieldValues(): void {
    const tableArray = this.getTableRowsArray();
    if (!tableArray || tableArray.length === 0) {
      return;
    }

    tableArray.controls.forEach((rowControl) => {
      if (rowControl instanceof FormGroup) {
        this.dynamicFormFields.forEach((field: any) => {
          if (field.field_type === 'select' && field.options && Array.isArray(field.options) && field.options.length > 0) {
            this.normalizeSelectFieldValue(rowControl, field);
          }
        });
      }
    });
  }

  /**
   * Initialize the radio selected row map by checking which rows have true/selected values
   * This is called during initialization to populate the map with existing selected rows
   * Only handles radio buttons without options
   */
  private initializeRadioSelectedRowMap(): void {
    // Clear existing map
    this.radioSelectedRowMap = {};

    const tableArray = this.getTableRowsArray();
    if (!tableArray || tableArray.length === 0) {
      return;
    }

    // Find all radio fields (without options only)
    const radioFields = this.dynamicFormFields.filter((field: any) => 
      field.field_type === 'radio' && 
      (!field.options || !Array.isArray(field.options) || field.options.length === 0)
    );
    
    if (radioFields.length === 0) {
      return;
    }

    // For each radio field, find the row that has a true/selected value
    radioFields.forEach((field: any) => {
      const fieldKey = this.getFieldKey(field.field_name);

      // Check each row to find which one has the selected value
      // Also normalize form control values to match radio button values
      const expectedValue = this.getRadioButtonValue(field);
      
      tableArray.controls.forEach((rowControl, rowIndex) => {
        if (rowControl instanceof FormGroup) {
          const control = rowControl.get(fieldKey);
          if (control) {
            const value = control.value;
            // For radio without options, check if value is truthy (true/1 or string equivalents)
            // Handle both boolean/number and string values from HTML radio inputs
            const isSelected = value === true || value === 1 || 
                              String(value) === '1' || String(value) === 'true';

            // Normalize the form control value to match the radio button value
            // This ensures the radio button appears selected when using [formControlName]
            if (isSelected) {
              // Set the form control to the exact value that matches the radio button's [value]
              if (control.value !== expectedValue) {
                control.setValue(expectedValue, { emitEvent: false });
              }
              // Store in map
              this.radioSelectedRowMap[field.field_name] = rowIndex;
            } else {
              // Ensure non-selected rows have the correct false value
              const falseValue = field.data_type === 'number' ? 0 : false;
              if (control.value !== falseValue && control.value !== null && control.value !== undefined && control.value !== '') {
                control.setValue(falseValue, { emitEvent: false });
              }
            }
          }
        }
      });
    });
  }

  /**
   * Check if a row is the currently selected row for a radio field
   * @param rowIndex The actual index of the row in the form array
   * @param fieldName The name of the radio field
   * @returns true if this row is the selected row for the radio field
   */
  isRadioRowSelected(rowIndex: number, fieldName: string): boolean {
    // Find the field to get its original field_name (consistent with map key)
    const field = this.dynamicFormFields.find((f: any) => 
      f.field_name?.toLowerCase() === fieldName?.toLowerCase()
    );
    
    // Use the field's original field_name as the map key
    const mapKey = field?.field_name || fieldName;
    const selectedRowIndex = this.radioSelectedRowMap[mapKey];
    
    return selectedRowIndex === rowIndex;
  }

  /**
   * Get the radio button value for radio buttons without options
   * Returns the appropriate value based on data_type (number returns 1, others return true)
   * Note: HTML radio buttons convert values to strings, so this will be "1" or "true"
   */
  getRadioButtonValue(field: any): any {
    if (field.data_type === 'number') {
      return 1;
    }
    return true;
  }
  
  
  
  getVisibleTableRows() {
    const tableArray = this.getTableRowsArray();
    
    // Simple cache: only recalculate if array length changed
    const currentLength = tableArray.length;
    if (currentLength === this.lastFormArrayLength && this.cachedVisibleRows.length > 0) {
      return this.cachedVisibleRows;
    }
    
    // Recalculate visible rows
    const visibleRows: { control: any; index: number }[] = [];
    
    tableArray.controls.forEach((control: any, actualIndex) => {
      const isDeletedControl = control.get('is_deleted');
      const isDeleted = isDeletedControl?.value === true || isDeletedControl?.value === 1 || isDeletedControl?.value === '1';
      if (!isDeleted) {
        visibleRows.push({ control, index: actualIndex });
      }

      if (this.showSelectionCheckbox) {
        const selectedControl = control.get('_selected');
        if (!selectedControl) {
          control.addControl('_selected', this.fb.control(false));
        }
      }
    });
    
    // Update cache
    this.cachedVisibleRows = visibleRows;
    this.lastFormArrayLength = currentLength;
    
    return visibleRows;
  }
  
  getVisibleTableRowsCount(): number {
    return this.getVisibleTableRows().length;
  }

  /**
   * Check if a row is the first visible row (for tag display)
   * @param rowIndex The actual index of the row in the form array
   * @returns true if this row is the first visible row
   */
  isFirstVisibleRow(rowIndex: number): boolean {
    const visibleRows = this.getVisibleTableRows();
    if (visibleRows.length === 0) {
      return false;
    }
    return visibleRows[0].index === rowIndex;
  }

  getFieldKey(fieldName: string): string {
    return fieldName?.toLowerCase().replace(/[^a-z0-9]/g, '_');
  }
  
  getColumnWidth(field: any): string {
    if (!field) return 'auto';
    
    // Use width property if available, otherwise use min-width
    const fieldWidth = Number(field.width) || 1;
    // Each unit of width = ~100px base, but scale it
    // Search and autocomplete fields get wider base width
    const baseWidth = (field.field_type === 'autocomplete') ? 150 : 100;
    const calculatedWidth = fieldWidth * baseWidth;
    
    return `${calculatedWidth}px`;
  }
  
  getColumnMinWidth(field: any): string {
    if (!field) return '100px';
  
    const widthMultiplier = field.width ? Number(field.width) : 1;
  
    let baseWidth = 120; // default fallback
  
    switch (field.field_type) {
      case 'checkbox':
        baseWidth = 60;
        break;
      case 'date':
      case 'datetime':
        baseWidth = 120;
        break;
      case 'number':
        baseWidth = 100;
        break;
      case 'select':
        baseWidth = 150;
        break;
      case 'search':
      case 'autocomplete':
        baseWidth = 250;
        break;
      case 'textarea':
        baseWidth = 200;
        break;
    }
    return `${baseWidth * widthMultiplier}px`;
  }  
  
  getSearchSuggestions(rowIndex: number, field: any): any[] {
    // Get row-specific options from suggestionsMap
    const key = this.getRowFieldKey(rowIndex, field.field_name);
    const rowSpecificOptions = this.suggestionsMap?.get(key);
    
    // Return row-specific options if available, otherwise fall back to field.options
    return rowSpecificOptions || field.options || [];
  }

  /**
   * Format a value for display in autocomplete template
   * Handles dates, arrays, objects, primitives, and edge cases
   */
  private formatItemValue(value: any): string {
    // Check for undefined, null, or string representations
    if (value === undefined || value === null || 
        String(value).toLowerCase() === 'undefined' || 
        String(value).toLowerCase() === 'null' ||
        String(value).trim() === '') {
      return '';
    }

    // Handle Date objects
    if (value instanceof Date) {
      return moment(value).format('DD/MM/YYYY');
    }

    // Handle date strings (ISO format, YYYY-MM-DD, or other date formats)
    if (typeof value === 'string' && value.trim() !== '') {
      const trimmed = value.trim();
      let dateValue = moment(trimmed, moment.ISO_8601, true);
      if (!dateValue.isValid()) {
        if (trimmed.toLowerCase() !== 'undefined' && trimmed.toLowerCase() !== 'null') {
          return trimmed;
        }
      } else {
        return dateValue.format('DD/MM/YYYY');
      }
    }

    // Handle arrays
    if (Array.isArray(value) && value.length > 0) {
      return value.join(', ');
    }

    // Handle objects (but not Date objects which are already handled)
    if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
      return JSON.stringify(value);
    }

    // Handle numbers and booleans
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return '';
  }

  /**
   * Get top-right text for autocomplete item
   * Uses field.autocompleteTopRightField configuration from parent module
   */
  getItemTopRightText(item: any, field: any): string {
    if (!item || !field?.autocompleteTopRightField) return '';
    return this.formatItemValue(item[field.autocompleteTopRightField]);
  }

  /**
   * Get bottom-right text for autocomplete item
   * Uses field.autocompleteBottomRightField configuration from parent module
   */
  getItemBottomRightText(item: any, field: any): string {
    if (!item || !field?.autocompleteBottomRightField) return '';
    return this.formatItemValue(item[field.autocompleteBottomRightField]);
  }
  
  /**
   * Get row-specific options for select and radio fields
   * Uses optionsMap if available and not empty, otherwise falls back to field.options
   */
  getFieldOptions(rowIndex: number, field: any): any[] {
    // Get row-specific options from optionsMap
    const key = this.getRowFieldKey(rowIndex, field.field_name);
    const rowSpecificOptions = this.optionsMap?.get(key);
    
    // Return row-specific options if available and not empty, otherwise fall back to field.options
    if (rowSpecificOptions && Array.isArray(rowSpecificOptions) && rowSpecificOptions.length > 0) {
      return rowSpecificOptions;
    }
    
    // Fallback to field-level options
    return field.options || [];
  }
  
  getRowFieldKey(rowIndex: number, fieldName: string): string {
    return `${rowIndex}_${fieldName}`;
  }

  /**
   * Populate optionsMap for all rows from field.options
   * This ensures that select/radio fields have their options available in optionsMap
   */
  private populateOptionsMapForAllRows(): void {
    if (!this.optionsMap || this.dynamicFormFields.length === 0) {
      return;
    }

    const tableArray = this.getTableRowsArray();
    if (!tableArray || tableArray.length === 0) {
      return;
    }

    // Iterate through all rows
    tableArray.controls.forEach((rowControl, rowIndex) => {
      // Iterate through all fields
      this.dynamicFormFields.forEach((field: any) => {
        // Only populate options for select and radio fields
        if (field.field_type === 'select' || field.field_type === 'radio') {
          // Only populate if field has options
          if (field.options && Array.isArray(field.options) && field.options.length > 0) {
            const key = this.getRowFieldKey(rowIndex, field.field_name);
            // Skip if parent already set custom options for this row+field (via optionsMap input)
            if (!this.optionsMap.has(key)) {
              this.optionsMap.set(key, field.options);
            }

            // Normalize select field values to match option values
            if (field.field_type === 'select' && rowControl instanceof FormGroup) {
              this.normalizeSelectFieldValue(rowControl, field);
            }
          }
        }
      });
    });
  }

  /**
   * Normalize select field value to match option value format
   * This ensures form control values match option.value so p-select can correctly preselect
   */
  private normalizeSelectFieldValue(rowControl: FormGroup, field: any): void {
    const fieldKey = this.getFieldKey(field.field_name);
    const control = rowControl.get(fieldKey);
    
    if (!control || !field.options || field.options.length === 0) {
      return;
    }
    
    const currentValue = control.value;
    
    // If value is null/undefined/empty, no need to normalize
    if (currentValue === null || currentValue === undefined || currentValue === '') {
      return;
    }
    
    // Try to find matching option
    const matchingOption = field.options.find((opt: any) => {
      const optValue = opt.value !== undefined ? opt.value : (opt.id !== undefined ? opt.id : opt);
      
      // Strict equality
      if (optValue === currentValue) {
        return true;
      }
      
      // String comparison
      if (String(optValue) === String(currentValue)) {
        return true;
      }
      
      // Also check label/name matching for all fields
      // This handles cases where API sends string labels but options have numeric values
      const optLabel = String(opt.label || opt.name || '').toUpperCase().trim();
      const currentStr = String(currentValue).toUpperCase().trim();
      if (optLabel === currentStr && optLabel !== '') {
        return true;
      }
      
      return false;
    });
    
    // If matching option found, normalize the value to match option.value exactly
    if (matchingOption) {
      const optionValue = matchingOption.value !== undefined ? matchingOption.value : (matchingOption.id !== undefined ? matchingOption.id : matchingOption);
      
      // Only update if values don't match exactly (strict equality)
      if (optionValue !== currentValue) {
        // Use patchValue to avoid triggering validation errors
        control.patchValue(optionValue, { emitEvent: false });
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Check if a radio option is selected
   * Handles type coercion to match form control value with option value
   * This is needed because HTML radio inputs compare values as strings, but form controls might store different types
   */
  isRadioOptionSelected(rowGroup: FormGroup, fieldName: string, optionValue: any): boolean {
    const fieldKey = this.getFieldKey(fieldName);
    const control = rowGroup?.get(fieldKey);
    if (!control) {
      return false;
    }
    
    const controlValue = control.value;
    
    // Handle null/undefined/empty
    if (controlValue == null && optionValue == null) {
      return true;
    }
    if (controlValue == null || optionValue == null) {
      return false;
    }
    
    // Strict equality check first (handles same type and value)
    if (controlValue === optionValue) {
      return true;
    }
    
    // Convert both to strings for comparison (HTML radio inputs use string values)
    const controlStr = String(controlValue).trim();
    const optionStr = String(optionValue).trim();
    
    // String comparison
    if (controlStr === optionStr) {
      return true;
    }
    
    // Handle boolean values
    if (controlValue === true) {
      return optionValue === true || optionValue === 'true' || optionValue === 1 || optionValue === '1';
    }
    if (controlValue === false) {
      return optionValue === false || optionValue === 'false' || optionValue === 0 || optionValue === '0' || optionValue === null || optionValue === '';
    }
    
    // Handle number/string coercion (e.g., form control has 1 as number, option.value is "1" as string)
    const controlNum = Number(controlValue);
    const optionNum = Number(optionValue);
    if (!isNaN(controlNum) && !isNaN(optionNum) && controlNum === optionNum) {
      return true;
    }
    
    // Handle object values (e.g., if controlValue is an object with id/value property)
    if (typeof controlValue === 'object' && controlValue !== null) {
      const objValue = controlValue.id ?? controlValue.value ?? controlValue;
      return objValue === optionValue || String(objValue) === optionStr;
    }
    
    return false;
  }

  /**
   * Normalize radio button values to match option values
   * This ensures form control values match option.value format so radio buttons display correctly
   */
  private normalizeRadioButtonValues(): void {
    const tableArray = this.getTableRowsArray();
    if (!tableArray) {
      return;
    }

    tableArray.controls.forEach((rowGroup, rowIndex) => {
      if (!(rowGroup instanceof FormGroup)) {
        return;
      }

      this.dynamicFormFields.forEach(field => {
        if (field.field_type !== 'radio') {
          return;
        }

        const fieldKey = this.getFieldKey(field.field_name);
        const control = rowGroup.get(fieldKey);
        if (!control) {
          return;
        }

        const currentValue = control.value;
        if (currentValue == null) {
          return;
        }

        // Get options for this field
        const options = this.getFieldOptions(rowIndex, field);
        if (!options || options.length === 0) {
          return;
        }

        // Find matching option by value (with type coercion)
        const matchingOption = options.find((opt: any) => {
          const optValue = opt.value;
          // Check exact match
          if (currentValue === optValue) {
            return true;
          }
          // Check string comparison
          if (String(currentValue) === String(optValue)) {
            return true;
          }
          // Check number comparison
          const currentNum = Number(currentValue);
          const optNum = Number(optValue);
          if (!isNaN(currentNum) && !isNaN(optNum) && currentNum === optNum) {
            return true;
          }
          return false;
        });

        // If we found a matching option, normalize the value to match option.value exactly
        if (matchingOption && currentValue !== matchingOption.value) {
          control.setValue(matchingOption.value, { emitEvent: false });
        }
      });
    });
  }
  
  onSearchQuery(event: any, rowIndex: number, field: any, rowGroup?: FormGroup) {
    const query = event.query || '';
    const fieldName = field.field_name;
    const rowsArray = this.getTableRowsArray();
    const rowId = (rowsArray.at(rowIndex) as FormGroup)?.get('id')?.value ?? null;
    
    // Emit search query to parent component
    // Parent component will update field.options in subTab.content
    this.searchQuery.emit({
      fieldName,
      query,
      rowIndex,
      rowId,
      rowGroup
    });
  }
  
  onSearchSelect(event: any, rowIndex: number, fieldName: string) {
    const rowsArray = this.getTableRowsArray();
    const rowGroup = rowsArray.at(rowIndex) as FormGroup;
    const rowId = rowGroup?.get('id')?.value ?? null;
    
    // Find the field to get optionLabel
    const field = this.dynamicFormFields.find((f: any) => 
      f.field_name?.toLowerCase() === fieldName?.toLowerCase()
    );
    const optionLabel = field?.optionLabel || 'label';
    
    const fieldKey = this.getFieldKey(fieldName);
    const control = rowGroup?.get(fieldKey);
    
    if (control && event) {
      const suggestions = this.getSearchSuggestions(rowIndex, field);
      const matchingOption = suggestions.find((opt: any) => {
        const optValue = opt.value ?? opt.id ?? opt;
        const eventValue = event.value ?? event.id ?? event;
        return optValue === eventValue || 
               (opt.id && event.id && opt.id === event.id) ||
               (opt.value && event.value && opt.value === event.value);
      });
      
      // Store the matching option from suggestions (exact reference) or the event object
      const valueToStore = matchingOption || event;
      control.setValue(valueToStore.value);
      
      // Force change detection to update the display
      this.cdr.detectChanges();
    }
    
    this.fieldChanged.emit({ 
      key: this.tableKey, 
      fieldName: fieldName,
      fieldValue: event, // Emit the full object
      value: rowsArray.value, 
      reason: 'table', 
      rowIndex, 
      rowId 
    });
  }
  
  getTableRowsArray(): FormArray {
    // If tableArray is provided and is a FormArray, return it
    if (this.tableArray && this.tableArray instanceof FormArray) {
      // Ensure it's connected to formGroup
      this.ensureTableArrayConnected();
      return this.tableArray;
    }
    
    // Fallback to formGroup control (only read, don't modify)
    const formArray = this.formGroup?.get(this.tableKey) as FormArray;
    if (formArray && formArray instanceof FormArray) {
      // Update tableArray reference if it's different
      if (this.tableArray !== formArray) {
        this.tableArray = formArray;
      }
      return formArray;
    }
    
    // If tableArray input is provided but not yet a FormArray, try to get it from formGroup
    if (this.tableArray && this.formGroup) {
      const formArrayFromGroup = this.formGroup.get(this.tableKey) as FormArray;
      if (formArrayFromGroup && formArrayFromGroup instanceof FormArray) {
        return formArrayFromGroup;
      }
    }
    
    // Return empty array if nothing exists (don't create during template evaluation)
    return this.fb.array([]);
  }
  
  addTableRow() {
    const row = this.fb.group({});
    
    row.addControl('id', this.fb.control(null));
    row.addControl('is_deleted', this.fb.control(false));
    
    // Add selection checkbox control if enabled
    if (this.showSelectionCheckbox) {
      row.addControl('_selected', this.fb.control(false));
    }
    
    // Use dynamicFormFields instead of data
    this.dynamicFormFields.forEach((field: any) => {
      const key = this.getFieldKey(field.field_name);
      const def = this.getDefaultValueForDataType(field.data_type);
      row.addControl(key, this.fb.control(def));
    });

    // Apply validators conditionally based on the row's selection state.
    // New rows in selection mode default to unselected, so they start unvalidated.
    this.applyValidatorsForRow(row, { updateValidity: false });

    const tableArray = this.getTableRowsArray();
    tableArray.push(row);
    
    // Populate optionsMap for the new row
    const newRowIndex = tableArray.length - 1;
    this.populateOptionsMapForRow(newRowIndex);
    
    // Invalidate cache
    this.cachedVisibleRows = [];
    this.lastFormArrayLength = tableArray.length;
    
    // Force the formArray to update
    tableArray.updateValueAndValidity({ emitEvent: false });
  
    this.emitTableChange();

    // Emit rowAdded event so parent can handle new row initialization (e.g., prefill values)
    this.rowAdded.emit({
      rowIndex: newRowIndex,
      rowControl: row,
      tableKey: this.tableKey
    });
    // this.cdr.detectChanges();
  }

  /**
   * Populate optionsMap for a specific row
   */
  private populateOptionsMapForRow(rowIndex: number): void {
    if (!this.optionsMap || this.dynamicFormFields.length === 0) {
      return;
    }

    // Iterate through all fields
    this.dynamicFormFields.forEach((field: any) => {
      // Only populate options for select and radio fields
      if (field.field_type === 'select' || field.field_type === 'radio') {
        // Only populate if field has options
        if (field.options && Array.isArray(field.options) && field.options.length > 0) {
          const key = this.getRowFieldKey(rowIndex, field.field_name);
          // Only set from field.options if the parent hasn't already provided row-specific options
          if (!this.optionsMap.has(key)) {
            this.optionsMap.set(key, field.options);
          }
        }
      }
    });
  }

  deleteAllRows() {
    if (this.viewMode) {
      return;
    }

    const tableArray = this.getTableRowsArray();
    if (!tableArray || tableArray.length === 0) {
      return;
    }

    tableArray.controls.forEach(control => {
      const rowGroup = control as FormGroup;
      const isDeletedControl = rowGroup.get('is_deleted');
      if (isDeletedControl) {
        isDeletedControl.setValue(true, { emitEvent: false });
      } else {
        rowGroup.addControl('is_deleted', this.fb.control(true));
      }

      if (this.showSelectionCheckbox) {
        const selectedControl = rowGroup.get('_selected');
        if (selectedControl) {
          selectedControl.setValue(false, { emitEvent: false });
        } else {
          rowGroup.addControl('_selected', this.fb.control(false));
        }
      }
    });

    // Invalidate cache
    this.cachedVisibleRows = [];
    this.lastFormArrayLength = tableArray.length;

    tableArray.updateValueAndValidity({ emitEvent: false });
    
    // Emit field change event with is_deleted fieldName so parent can update summary
    this.fieldChanged.emit({
      key: this.tableKey,
      value: tableArray.value,
      fieldName: 'is_deleted',
      rowIndex: -1, // -1 indicates all rows were affected
      fieldValue: true
    });
    
    if (this.showSelectionCheckbox) {
      this.emitSelectionChange();
    }
    this.cdr.detectChanges();
  }

  duplicateLastRow() {
    if (this.viewMode) {
      return;
    }

    const visibleRows = this.getVisibleTableRows();
    if (!visibleRows || visibleRows.length === 0) {
      return;
    }

    const sourceRow = visibleRows[visibleRows.length - 1].control as FormGroup;
    const sourceControls = Object.keys(sourceRow.controls);

    this.addTableRow();

    const tableArray = this.getTableRowsArray();
    const newRow = tableArray.at(tableArray.length - 1) as FormGroup;

    sourceControls.forEach(key => {
      if (key === 'id') {
        newRow.get(key)?.setValue(null, { emitEvent: false });
        return;
      }

      if (key === 'is_deleted') {
        newRow.get(key)?.setValue(false, { emitEvent: false });
        return;
      }

      if (key === '_selected') {
        if (this.showSelectionCheckbox) {
          newRow.get(key)?.setValue(false, { emitEvent: false });
        }
        return;
      }

      const sourceControl = sourceRow.get(key);
      const targetControl = newRow.get(key);
      if (sourceControl && targetControl) {
        const value = sourceControl.value;
        if (value instanceof Date) {
          targetControl.setValue(new Date(value.getTime()), { emitEvent: false });
        } else if (Array.isArray(value)) {
          targetControl.setValue([...value], { emitEvent: false });
        } else if (value && typeof value === 'object') {
          targetControl.setValue({ ...value }, { emitEvent: false });
        } else {
          targetControl.setValue(value, { emitEvent: false });
        }
      }
    });

    newRow.updateValueAndValidity({ emitEvent: false });
    tableArray.updateValueAndValidity({ emitEvent: false });
    this.emitTableChange();
    this.cdr.detectChanges();

    this.rowDuplicated.emit({
      sourceRowIndex: visibleRows[visibleRows.length - 1].index,
      newRowIndex: tableArray.length - 1,
      newRowControl: newRow,
      tableKey: this.tableKey
    });
  }

  /**
   * Duplicates a specific row at the given index.
   * Used for splitting items (e.g., when creating GRN from PO with different batch details).
   * The new row will have the same values as the source row except:
   * - id will be null (new record)
   * - quantity will be reset to 0 (user needs to specify split quantity)
   * - is_deleted will be false
   * - _selected will be false (if checkbox is enabled)
   */
  duplicateRow(sourceIndex: number) {
    if (this.viewMode) {
      return;
    }

    const tableArray = this.getTableRowsArray();
    if (!tableArray || sourceIndex < 0 || sourceIndex >= tableArray.length) {
      return;
    }

    const sourceRow = tableArray.at(sourceIndex) as FormGroup;
    if (!sourceRow || sourceRow.get('is_deleted')?.value === true) {
      return;
    }

    const sourceControls = Object.keys(sourceRow.controls);

    // Create new row with default values
    this.addTableRow();

    const newRowIndex = tableArray.length - 1;
    const newRow = tableArray.at(newRowIndex) as FormGroup;

    // Copy values from source row
    sourceControls.forEach(key => {
      // Reset id for new record
      if (key === 'id') {
        newRow.get(key)?.setValue(null, { emitEvent: false });
        return;
      }

      // Ensure is_deleted is false
      if (key === 'is_deleted') {
        newRow.get(key)?.setValue(false, { emitEvent: false });
        return;
      }

      // Reset selection
      if (key === '_selected') {
        if (this.showSelectionCheckbox) {
          newRow.get(key)?.setValue(false, { emitEvent: false });
        }
        return;
      }

      // Reset quantity to 0 for split - user needs to specify
      if (key === 'quantity') {
        newRow.get(key)?.setValue(0, { emitEvent: false });
        return;
      }

      // Copy all other values from source
      const sourceControl = sourceRow.get(key);
      const targetControl = newRow.get(key);
      if (sourceControl && targetControl) {
        const value = sourceControl.value;
        if (value instanceof Date) {
          targetControl.setValue(new Date(value.getTime()), { emitEvent: false });
        } else if (Array.isArray(value)) {
          targetControl.setValue([...value], { emitEvent: false });
        } else if (value && typeof value === 'object') {
          targetControl.setValue({ ...value }, { emitEvent: false });
        } else {
          targetControl.setValue(value, { emitEvent: false });
        }
      }
    });

    newRow.updateValueAndValidity({ emitEvent: false });
    tableArray.updateValueAndValidity({ emitEvent: false });

    // Invalidate cache
    this.cachedVisibleRows = [];
    this.lastFormArrayLength = tableArray.length;

    this.emitTableChange();

    // Emit rowDuplicated event so parent can handle additional logic
    this.rowDuplicated.emit({
      sourceRowIndex: sourceIndex,
      newRowIndex: newRowIndex,
      newRowControl: newRow,
      tableKey: this.tableKey
    });

    this.cdr.detectChanges();
  }

  private emitTableChange() {
    this.fieldChanged.emit({
      key: this.tableKey,
      value: this.getTableRowsArray().value
    });
  }
  
  
  private getDefaultValueForDataType(dataType: string): any {
    switch (dataType?.toLowerCase()) {
      case 'number':
      case 'decimal':
      case 'float':
      case 'double':
        return 0;
      case 'boolean':
        return false;
      case 'date':
      case 'datetime':
        return null;
      case 'array':
      case 'json':
        return [];
      case 'object':
        return {};
      default:
        return '';
    }
  }
  
  getTableCellDisplayValue(field: any, rowGroup: FormGroup, rowIndex?: number): string {
    // if (!this.viewMode && field.field_type !== 'label') return '';
    
    const control = rowGroup.get(this.getFieldKey(field.field_name));
    const value = control?.value;
    
    // Pass rowIndex for search/select/radio fields so formatDisplayValue can check suggestionsMap
    return this.formatDisplayValue(value, field, rowGroup, rowIndex);
  }

  isNonEditableField(field: any): boolean {
    return this.nonEditableFields && this.nonEditableFields.length > 0 && 
           this.nonEditableFields.includes(field.field_name);
  }

  getRedirectCommands(field: any, control?: FormGroup | null | undefined): any[] {
    // First try to get field-specific redirect_route from control (e.g., credit_transaction_no_redirect_route)
    let route = control?.get(`${field.field_name}_redirect_route`)?.value;

    // If not found, try generic redirect_route from control (row data)
    if (!route) {
      route = control?.get('redirect_route')?.value;
    }

    // If not in control, try field config
    if (!route) {
      route = field?.redirect_route;
    }

    if (Array.isArray(route)) {
      return route;
    }
    return [route];
  }

  getRedirectQueryParams(field: any, control: FormGroup | null | undefined): Record<string, any> {
    if (this.redirectQueryParamsResolver) {
      return this.redirectQueryParamsResolver(field, control);
    }

    // First try to get field-specific redirect_query_params from control (e.g., credit_transaction_no_redirect_query_params)
    let params = control?.get(`${field.field_name}_redirect_query_params`)?.value;

    // If not found, try generic redirect_query_params from control (row data)
    if (!params) {
      params = control?.get('redirect_query_params')?.value;
    }

    // If not in control, try field config
    if (!params) {
      params = field?.redirect_query_params;
    }

    return params ? { ...params } : {};
  }

  /**
   * Get the base href from the DOM or default to '/'
   * This handles apps deployed with different base hrefs (e.g., /accounting/, /demo/)
   * Similar to AuthInterceptor.getBaseHref()
   */
  private getBaseHref(): string {
    const baseTag = document.querySelector('base');
    if (baseTag && baseTag.getAttribute('href')) {
      const baseHref = baseTag.getAttribute('href') || '/';
      // Ensure it ends with / and remove trailing slash for path construction
      return baseHref.endsWith('/') ? baseHref.slice(0, -1) : baseHref;
    }
    return '';
  }

  // Open redirect link in new tab
  openRedirectInNewTab(field: any, control: FormGroup | null | undefined, event: Event): void {
    event.preventDefault();
    const commands = this.getRedirectCommands(field,control);
    const queryParams = this.getRedirectQueryParams(field, control);
    
    // Get base href to support apps deployed with different base paths (e.g., /accounting/, /demo/)
    const baseHref = this.getBaseHref();
    
    // Create URL tree with commands (without base href, router handles it)
    const urlTree = this.router.createUrlTree(commands, { queryParams });
    const serializedUrl = this.router.serializeUrl(urlTree);
    
    // Construct the full path with base href
    // If baseHref is '/accounting', we want '/accounting/vendors/purchase-invoice?invoiceId=...'
    const pathWithBase = baseHref ? `${baseHref}${serializedUrl}` : serializedUrl;
    
    // Get the full base href URL (including protocol and hostname) for constructing absolute URL
    const baseHrefUrl = document.querySelector('base')?.href || window.location.origin;
    const fullUrl = new URL(pathWithBase, baseHrefUrl).href;
    
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }

  private formatDisplayValue(value: any, field: any, rowGroup?: FormGroup, rowIndex?: number): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (field.field_type) {
      case 'select': { 
        // Use row-specific options if rowIndex is provided, otherwise fall back to field.options
        const options = rowIndex !== undefined ? this.getFieldOptions(rowIndex, field) : (field.options || []);
        const option = options.find((opt: any) => {
          const optValue = opt.value ?? opt.id ?? opt;
          return optValue === value || optValue === value?.id || optValue === value?.value;
        });
        const optionLabel = field.optionLabel || 'label';
        return option?.[optionLabel] || option?.label || option?.name || value?.label || value?.name || value;
      }
      case 'label': { 
        // Check if the value is a date and format it as dd/MM/yyyy
        if (value instanceof Date) {
          return moment(value).format('DD/MM/YYYY');
        } else if (typeof value === 'string') {
          // Check if it's a date string (either by data_type or by parsing)
          if (field.data_type === 'date' || field.data_type === 'datetime') {
            const momentDate = moment(value);
            if (momentDate.isValid()) {
              return momentDate.format('DD/MM/YYYY');
            }
          } else {
            // Try to parse as date even if data_type isn't explicitly date
            const momentDate = moment(value);
            if (momentDate.isValid() && momentDate.format('YYYY-MM-DD') === value.substring(0, 10)) {
              return momentDate.format('DD/MM/YYYY');
            }
          }
        }
        
        const options = rowIndex !== undefined ? this.getFieldOptions(rowIndex, field) : (field.options || []);
        const option = options.find((opt: any) => {
          const optValue = opt.value ?? opt.id ?? opt;
          return optValue === value || optValue === value?.id || optValue === value?.value;
        });
        const optionLabel = field.optionLabel || 'label';
        return option?.[optionLabel] || option?.label || option?.name || value?.label || value?.name || value;
      }
      case 'checkbox':
        return value ? 'Yes' : 'No';
      
      case 'date':
        if (value instanceof Date) {
          return moment(value).format('DD/MM/YYYY');
        } else if (typeof value === 'string') {
          const momentDate = moment(value);
          return momentDate.isValid() ? momentDate.format('DD/MM/YYYY') : value;
        }
        return value;
      
      case 'datetime':
        if (value instanceof Date) {
          return moment(value).format('HH:mm');
        } else if (typeof value === 'string') {
          const momentDate = moment(value, ['HH:mm', 'HH:mm:ss', 'YYYY-MM-DD HH:mm:ss']);
          return momentDate.isValid() ? momentDate.format('HH:mm') : value;
        }
        return value;
      
      case 'radio': {
        // Use row-specific options if rowIndex is provided, otherwise fall back to field.options
        const options = rowIndex !== undefined ? this.getFieldOptions(rowIndex, field) : (field.options || []);
        if (options.length > 0) {
          const option = options.find((opt: any) => {
            const optValue = opt.value ?? opt.id ?? opt;
            return optValue === value || optValue === value?.id || optValue === value?.value;
          });
          return option?.label || option?.name || value?.toString() || String(value);
        }
        // For boolean radio buttons without options
        return value?.toString() || String(value);
      }
      
      case 'number': {

        if (typeof value === 'number' && this.shouldFormatCurrency(field)) {
          return this.formatCurrencyWithUSD(value);
        }

        return value;
      }
      case 'search':
      case 'autocomplete':
        return typeof value === 'object' ? value?.label || value?.name || value?.item_name : value;

      default:
        return value?.toString() || String(value);
    }
  }
  
  onTableFieldChange(rowIndex: number, fieldName: string, event: any) {
    const value = event?.value ?? event?.target?.value ?? event;
    const rowsArray = this.getTableRowsArray();
    const rowId = (rowsArray.at(rowIndex) as FormGroup)?.get('id')?.value ?? null;
    
    // Find the field definition to check if it's a radio field
    const field = this.dynamicFormFields.find((f: any) => 
      f.field_name?.toLowerCase() === fieldName?.toLowerCase()
    );
    
    // Handle radio button (without options only): use map to track selected row and update accordingly
    if (field && field.field_type === 'radio') {
      const fieldKey = this.getFieldKey(fieldName);
      const currentRowControl = rowsArray.at(rowIndex) as FormGroup;
      const currentControl = currentRowControl?.get(fieldKey);
      
      if (currentControl) {        
        // Get the value from the event (radio button value)
        // HTML radio inputs always return string values, so we need to convert them
        let newValue: any;
        
        // Handle event.target.value (string from HTML radio input)
        if (event?.target?.value !== undefined) {
          // Convert string to appropriate type based on data_type
          if (field.data_type === 'number') {
            newValue = 1;
          } else {
            newValue = true;
          }
        } else {
          // Fallback: use the value directly and normalize it
          const normalizedValue = String(value);
          if (field.data_type === 'number') {
            newValue = (normalizedValue === '1' || normalizedValue === 'true') ? 1 : 0;
          } else {
            newValue = (normalizedValue === 'true' || normalizedValue === '1') ? true : false;
          }
        }
        
        // Check if the radio is being selected (true/1) or deselected (false/0)
        const isSelected = newValue === true || newValue === 1;
        
        if (isSelected) {
          // Use the field's original field_name for the map key (consistent with initialization)
          const mapKey = field.field_name;
          
          // Get the previously selected row index for this field
          const previousSelectedRowIndex = this.radioSelectedRowMap[mapKey];
          
          // Update the map with the new selected row index IMMEDIATELY
          this.radioSelectedRowMap[mapKey] = rowIndex;
          
          // Set current row to true/selected value
          currentControl.setValue(newValue, { emitEvent: false });
          
          // If there was a previously selected row, set it to false/0
          if (previousSelectedRowIndex !== undefined && previousSelectedRowIndex !== rowIndex) {
            const previousRowControl = rowsArray.at(previousSelectedRowIndex) as FormGroup;
            if (previousRowControl) {
              const previousControl = previousRowControl.get(fieldKey);
              if (previousControl) {
                const falseValue = field.data_type === 'number' ? 0 : false;
                previousControl.setValue(falseValue, { emitEvent: false });
              }
            }
          }
          
          // Force change detection
          this.cdr.detectChanges();
        } else {
          // If deselecting (setting to false/0), remove from map and update control
          const mapKey = field.field_name;
          if (this.radioSelectedRowMap[mapKey] === rowIndex) {
            delete this.radioSelectedRowMap[mapKey];
          }
          currentControl.setValue(newValue, { emitEvent: false });
          this.cdr.detectChanges();
        }
      }
    }

    // applying lob logic(s)
    const currentRowControl = rowsArray.at(rowIndex) as FormGroup;
    if (fieldName === 'line_of_business_id' || fieldName == 'line_of_business') {
      this.applyLineOfBusinessLogic(currentRowControl, value);
    }

    // Clamp decimal fields to 2 decimal places
    let emittedValue: any = value;
    if (field && (field.field_type === 'double' || field.field_type === 'decimal' || field.field_type === 'float')) {
      const ctrl = currentRowControl?.get(this.getFieldKey(fieldName));
      const raw = ctrl?.value;
      const num = Number(raw);
      if (raw != null && raw !== '' && !Number.isNaN(num)) {
        const rounded = Math.round(num * 100) / 100;
        if (rounded !== num) {
          ctrl?.setValue(rounded, { emitEvent: false });
          emittedValue = rounded;
        }
      }
    }

    this.fieldChanged.emit({
      key: this.tableKey,
      fieldName: fieldName,
      fieldValue: emittedValue,
      value: rowsArray.value,
      reason: 'table',
      rowIndex,
      rowId
    });
  }

  toggleRadioButton(rowIndex: number, field: any) {
    const rowsArray = this.getTableRowsArray();
    const rowGroup = rowsArray.at(rowIndex) as FormGroup;
    const fieldKey = this.getFieldKey(field.field_name);
    const control = rowGroup.get(fieldKey);
    
    if (control) {
      // Toggle between true/false or 1/0
      const currentValue = control.value;
      const newValue = !currentValue || currentValue === 0 || currentValue === '0' || currentValue === false 
        ? (field.data_type === 'number' ? 1 : true)
        : (field.data_type === 'number' ? 0 : false);
      
      control.setValue(newValue);
      this.onTableFieldChange(rowIndex, field.field_name, newValue);
    }
  }

  getToggleButtonState(rowGroup: FormGroup, field: any): boolean {
    const fieldKey = this.getFieldKey(field.field_name);
    const control = rowGroup.get(fieldKey);
    const value = control?.value;
    
    return !!(value === true || value === 1 || value === '1' || value === 'true');
  }

  getToggleButtonLabel(rowGroup: FormGroup, field: any): string {
    const isActive = this.getToggleButtonState(rowGroup, field);
    const displayName = field.display_name || field.field_name;

    // Return label based on state, or use field display name
    if (isActive) {
      return displayName;
    } else {
      return displayName;
    }
  }

  onFileSelected(event: any, field: any, rowIndex: number) {
    const fileInput = event.target;
    const files = fileInput.files;

    if (files && files.length > 0) {
      // For S3 upload, we handle one file at a time
      // If multiple files selected, only upload the first one
      const file = files[0] as File;
      const tableArray = this.getTableRowsArray();
      const rowGroup = tableArray.at(rowIndex) as FormGroup;
      const rowId = rowGroup?.get('id')?.value ?? null;

      // Emit file upload request event for S3 upload
      // The parent component will handle the S3 upload and update the form control
      this.fileUploadRequested.emit({
        file: file,
        field: field,
        rowIndex: rowIndex,
        rowId: rowId
      });
    }
  }

  getSelectedFileName(field: any, rowIndex: number): string {
    const tableArray = this.getTableRowsArray();
    const rowGroup = tableArray.at(rowIndex) as FormGroup;

    if (!rowGroup) {
      return '';
    }

    // First, check if there's a file_name field with the actual file name
    const fileNameFieldKey = this.getFieldKey('file_name');
    const fileNameControl = rowGroup.get(fileNameFieldKey);
    const fileNameValue = fileNameControl?.value;
    
    // If file_name field has a value, use it (this is the actual file name)
    if (fileNameValue && typeof fileNameValue === 'string' && fileNameValue.length > 0) {
      return fileNameValue;
    }

    // Fallback: check the file field itself
    const fieldKey = this.getFieldKey(field.field_name);
    const control = rowGroup.get(fieldKey);
    const value = control?.value;

    if (!value) {
      return '';
    }

    if (field.multiple && Array.isArray(value)) {
      return value.map((file: File) => file.name).join(', ');
    } else if (value instanceof File) {
      return value.name;
    } else if (typeof value === 'string' && value.length > 0) {
      // Handle s3_key or file path - extract filename from path
      const parts = value.split('/');
      return parts[parts.length - 1] || value;
    }

    return '';
  }

  removeFile(field: any, rowIndex: number, fileInput: any) {
    const fieldKey = this.getFieldKey(field.field_name);
    const tableArray = this.getTableRowsArray();
    const rowGroup = tableArray.at(rowIndex) as FormGroup;

    if (rowGroup) {
      const control = rowGroup.get(fieldKey);
      if (control) {
        control.setValue(null);
        control.markAsTouched();
      }
    }

    // Clear the file input
    if (fileInput) {
      fileInput.value = '';
    }

    // Emit field change event
    this.fieldChanged.emit({
      key: this.tableKey,
      value: tableArray.value,
      reason: 'table',
      rowIndex: rowIndex,
      fieldName: field.field_name,
      fieldValue: null
    });
  }

  getTableFieldKey(rowIndex: number, fieldName: string): string {
    return `${this.tableKey}_${rowIndex}_${this.getFieldKey(fieldName)}`;
  }

  /**
   * Check if a file field has a value (s3_key) that can be downloaded
   */
  hasFileValue(rowIndex: number, fieldName: string): boolean {
    const tableArray = this.getTableRowsArray();
    const rowGroup = tableArray.at(rowIndex) as FormGroup;
    if (!rowGroup) {
      return false;
    }
    const fieldKey = this.getFieldKey(fieldName);
    const control = rowGroup.get(fieldKey);
    const value = control?.value;
    return value && typeof value === 'string' && value.length > 0;
  }

  /**
   * Open download URL in new tab
   */
  openDownloadUrl(rowIndex: number, fieldName: string): void {
    const tableArray = this.getTableRowsArray();
    const rowGroup = tableArray.at(rowIndex) as FormGroup;
    
    if (!rowGroup) {
      console.warn('Row not found for file download');
      return;
    }

    const fieldKey = this.getFieldKey(fieldName);
    const control = rowGroup.get(fieldKey);
    const s3Key = control?.value;

    if (!s3Key || typeof s3Key !== 'string' || s3Key.length === 0) {
      console.warn('No S3 key found in the file field');
      return;
    }

    // Get the field definition
    const field = this.dynamicFormFields.find(f => f.field_name === fieldName);

    // Get row ID if available
    const idControl = rowGroup.get('id');
    const rowId = idControl?.value;

    // Emit event to parent to handle download URL fetching
    this.fileDownloadRequested.emit({
      s3Key: s3Key,
      fieldName: fieldName,
      field: field,
      rowIndex: rowIndex,
      rowId: rowId
    });
  }

  removeTableRow(actualIndex: number) {
    const tableArray = this.getTableRowsArray();
    
    if (actualIndex < 0 || actualIndex >= tableArray.length) {
      return;
    }
    
    const rowGroup = tableArray.at(actualIndex) as FormGroup;
    if (!rowGroup) {
      return;
    }
    
    const idControl = rowGroup.get('id');
    const rowId = idControl?.value;
    const isDeletedControl = rowGroup.get('is_deleted');
    const selectedControl = rowGroup.get('_selected');
    
    // Check if row has an ID (existing row from database)
    if (rowId !== null && rowId !== undefined && rowId !== '') {
      // Existing row with ID - mark as deleted (soft delete) instead of removing
      if (isDeletedControl) {
        isDeletedControl.setValue(true, { emitEvent: false });
      } else {
        rowGroup.addControl('is_deleted', this.fb.control(true));
      }

      if (selectedControl) {
        selectedControl.setValue(false, { emitEvent: false });
      } else {
        rowGroup.addControl('_selected', this.fb.control(false));
      }
      
      // If this row was selected for any radio field, just remove from map (don't auto-select another row)
      Object.keys(this.radioSelectedRowMap).forEach((fieldName) => {
        if (this.radioSelectedRowMap[fieldName] === actualIndex) {
          // Find the field definition
          const field = this.dynamicFormFields.find((f: any) => 
            f.field_name?.toLowerCase() === fieldName?.toLowerCase() && f.field_type === 'radio'
          );
          
          if (field) {
            // Just remove from map - don't auto-select another row
            delete this.radioSelectedRowMap[fieldName];
          } else {
            // Field not found, just remove from map
            delete this.radioSelectedRowMap[fieldName];
          }
        }
      });
    } else {
      // New row without ID - actually remove it from the array
      tableArray.removeAt(actualIndex);

      this.fieldChanged.emit({
        key: this.tableKey,
        value: this.getTableRowsArray().value,
        reason: 'row_delete',
        rowIndex: actualIndex
      });
      
      // Update radio selected row map: remove entries for this index and adjust indices for rows after it
      Object.keys(this.radioSelectedRowMap).forEach((fieldName) => {
        const selectedRowIndex = this.radioSelectedRowMap[fieldName];
        if (selectedRowIndex === actualIndex) {
          // The deleted row was selected - just remove from map, don't auto-select another row
          delete this.radioSelectedRowMap[fieldName];
        } else if (selectedRowIndex > actualIndex) {
          // Adjust index for rows after the deleted row
          this.radioSelectedRowMap[fieldName] = selectedRowIndex - 1;
        }
      });
    }
    
    // Invalidate cache
    this.cachedVisibleRows = [];
    this.lastFormArrayLength = tableArray.length;
    
    // Force the formArray to update
    tableArray.updateValueAndValidity({ emitEvent: false });
    
    // Force change detection to update radio button UI
    this.cdr.detectChanges();
    
    // Emit field change event with is_deleted fieldName so parent can update summary
    this.fieldChanged.emit({
      key: this.tableKey,
      value: tableArray.value,
      fieldName: 'is_deleted',
      rowIndex: actualIndex,
      fieldValue: true,
      rowId: rowId
    });
    
    // Emit selection change if selection checkbox is enabled
    if (this.showSelectionCheckbox) {
      this.emitSelectionChange();
    }
    // this.cdr.detectChanges();
  }

  getSelectedItems(): any[] {
    if (!this.showSelectionCheckbox) {
      return [];
    }
    
    const tableArray = this.getTableRowsArray();
    const selectedItems: any[] = [];
    
    tableArray.controls.forEach((control, index) => {
      const rowGroup = control as FormGroup;
      const isDeletedControl = rowGroup.get('is_deleted');
      const isDeleted = isDeletedControl?.value === true || isDeletedControl?.value === 1 || isDeletedControl?.value === '1';
      
      if (!isDeleted) {
        const selectedControl = rowGroup.get('_selected');
        if (selectedControl?.value === true) {
          // Get all form values except _selected and is_deleted
          const rowData: any = {};
          Object.keys(rowGroup.controls).forEach(key => {
            if (key !== '_selected' && key !== 'is_deleted') {
              const control = rowGroup.get(key);
              rowData[key] = control?.value;
            }
          });
          selectedItems.push(rowData);
        }
      }
    });
    
    return selectedItems;
  }

  getSelectedIndices(): number[] {
    if (!this.showSelectionCheckbox) {
      return [];
    }
    
    const tableArray = this.getTableRowsArray();
    const selectedIndices: number[] = [];
    
    tableArray.controls.forEach((control, index) => {
      const rowGroup = control as FormGroup;
      const isDeletedControl = rowGroup.get('is_deleted');
      const isDeleted = isDeletedControl?.value === true || isDeletedControl?.value === 1 || isDeletedControl?.value === '1';
      
      if (!isDeleted) {
        const selectedControl = rowGroup.get('_selected');
        if (selectedControl?.value === true) {
          selectedIndices.push(index);
        }
      }
    });
    
    return selectedIndices;
  }

  isAllSelected(): boolean {
    if (!this.showSelectionCheckbox) {
      return false;
    }
    
    const visibleRows = this.getVisibleTableRows();
    if (visibleRows.length === 0) {
      return false;
    }
    
    // Get only enabled checkboxes
    const enabledRows = visibleRows.filter(row => {
      const selectedControl = row.control.get('_selected');
      return selectedControl && !selectedControl.disabled;
    });
    
    if (enabledRows.length === 0) {
      return false;
    }
    
    return enabledRows.every(row => {
      const selectedControl = row.control.get('_selected');
      return selectedControl?.value === true;
    });
  }

  isIndeterminate(): boolean {
    if (!this.showSelectionCheckbox) {
      return false;
    }
    
    const visibleRows = this.getVisibleTableRows();
    if (visibleRows.length === 0) {
      return false;
    }
    
    // Get only enabled checkboxes
    const enabledRows = visibleRows.filter(row => {
      const selectedControl = row.control.get('_selected');
      return selectedControl && !selectedControl.disabled;
    });
    
    if (enabledRows.length === 0) {
      return false;
    }
    
    const selectedCount = enabledRows.filter(row => {
      const selectedControl = row.control.get('_selected');
      return selectedControl?.value === true;
    }).length;
    
    return selectedCount > 0 && selectedCount < enabledRows.length;
  }

  toggleSelectAll(event: any) {
    if (!this.showSelectionCheckbox) {
      return;
    }
    
    const isChecked = event?.target?.checked ?? event?.checked ?? false;
    const visibleRows = this.getVisibleTableRows();
    const rowsArray = this.getTableRowsArray();
    
    visibleRows.forEach((row, index) => {
      const selectedControl = row.control.get('_selected');
      // Only update enabled checkboxes, skip disabled ones
      if (selectedControl && !selectedControl.disabled) {
        selectedControl.setValue(isChecked, { emitEvent: false });

        // Re-apply validators so toggling select-all updates required state per row.
        if (row.control instanceof FormGroup) {
          this.applyValidatorsForRow(row.control);
        }

        // Emit fieldChanged event for each row to trigger calculations
        this.fieldChanged.emit({ 
          key: this.tableKey, 
          fieldName: '_selected',
          fieldValue: isChecked,
          value: rowsArray?.value, 
          reason: 'table', 
          rowIndex: row.index,
          rowId: row.control.get('id')?.value
        });
      }
    });
    
    this.emitSelectionChange();
    this.cdr.detectChanges();
  }

  onSelectionChange(rowIndex: number, event?: any) {
    if (!this.showSelectionCheckbox) {
      return;
    }
    
    // Emit fieldChanged event to trigger calculations
    const rowsArray = this.getTableRowsArray();
    if (rowsArray && rowsArray.controls[rowIndex]) {
      const rowControl = rowsArray.controls[rowIndex] as FormGroup;
      // Get the value from event target if available, otherwise from form control
      const selectedValue = event?.target?.checked !== undefined 
        ? event.target.checked 
        : rowControl.get('_selected')?.value;
      
      // Explicitly set the form control value to ensure it's updated
      const selectedControl = rowControl.get('_selected');
      if (selectedControl && selectedValue !== selectedControl.value) {
        selectedControl.setValue(selectedValue, { emitEvent: false });
      }

      // Re-apply validators so ticking re-adds required and unticking removes it.
      this.applyValidatorsForRow(rowControl);

      this.fieldChanged.emit({
        key: this.tableKey, 
        fieldName: '_selected',
        fieldValue: selectedValue,
        value: rowsArray.value, 
        reason: 'table', 
        rowIndex, 
        rowId: rowControl.get('id')?.value
      });
    }
    
    this.emitSelectionChange();
  }

  private emitSelectionChange() {
    if (!this.showSelectionCheckbox) {
      return;
    }
    
    const selectedIndices = this.getSelectedIndices();
    const selectedItems = this.getSelectedItems();
    
    this.selectionChanged.emit({
      selectedIndices,
      selectedItems
    });
  }

  /**
   * Calculate total for a specific field across all visible rows
   * If row selection is enabled, only sum selected rows; otherwise sum all rows
   * @param fieldName The field name to calculate total for
   * @returns The sum of all values for the specified field
   */
  calculateTotal(fieldName: string): number {
    if (!this.totalFields || this.totalFields.length === 0 || !this.totalFields.includes(fieldName)) {
      return 0;
    }

    const tableArray = this.getTableRowsArray();
    if (!tableArray || tableArray.length === 0) {
      return 0;
    }

    let total = 0;
    const fieldKey = this.getFieldKey(fieldName);

    tableArray.controls.forEach((control: any) => {
      if (control instanceof FormGroup) {
        // Check if row is deleted
        const isDeletedControl = control.get('is_deleted');
        const isDeleted = isDeletedControl?.value === true || 
                         isDeletedControl?.value === 1 || 
                         isDeletedControl?.value === '1';
        
        if (!isDeleted) {
          // If row selection is enabled, only include selected rows
          if (this.showSelectionCheckbox) {
            const selectedControl = control.get('_selected');
            const isSelected = selectedControl?.value === true || 
                              selectedControl?.value === 1 || 
                              selectedControl?.value === 'true' ||
                              String(selectedControl?.value).toLowerCase() === 'true';
            
            if (!isSelected) {
              return; // Skip unselected rows
            }
          }
          
          const fieldControl = control.get(fieldKey);
          if (fieldControl) {
            const value = fieldControl.value;
            // Convert to number and add to total
            const numValue = Number(value);
            if (!isNaN(numValue)) {
              total += numValue;
            }
          }
        }
      }
    });

    return total;
  }

  /**
   * Check if totals row should be displayed
   */
  shouldShowTotalsRow(): boolean {
    // If flag is explicitly set to false, never show totals row
    if (this.showTotalsRow === false) {
      return false;
    }

    // Otherwise, show totals row when there are total fields configured
    return this.totalFields && this.totalFields.length > 0;
  }

  /**
   * Check if a field should show total
   */
  shouldShowTotalForField(field: any): boolean {
    if (!this.totalFields || this.totalFields.length === 0) {
      return false;
    }
    return this.totalFields.includes(field.field_name);
  }

  /**
   * Get validators for a field based on its type, data type, and field name
   * Includes min(0) validation for numeric fields that should not allow negative values
   */
  private getValidatorsForField(field: any): any[] {
    const validators: any[] = [];
    
    // Add required validator if field is mandatory
    if (field.is_mandatory) {
      validators.push(Validators.required);
    }
    
    // Check if field should have non-negative validation
    if (this.isNumericField(field) && this.shouldHaveNonNegativeValidation(field)) {
      validators.push(Validators.min(0));
    }
    
    return validators;
  }

  /**
   * Check if a numeric field should have non-negative validation
   * Returns false for fields that are allowed to be negative (e.g., adjust_qty_by for inventory adjustments)
   */
  private shouldHaveNonNegativeValidation(field: any): boolean {
    if (!field) return false;
    
    const fieldName = (field.field_name || '').toLowerCase();
    
    // Fields that are allowed to be negative
    const allowNegativeFields = [
      'adjust_qty_by',      // Inventory adjustments - can be negative to remove stock
      'adjustment_amount',  // Manual adjustments might be negative
      'variance',           // Variances can be negative
      'difference',         // Differences can be negative
    ];
    
    // Check if this field is in the allow-negative list
    for (const negativeField of allowNegativeFields) {
      if (fieldName.includes(negativeField)) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Check if a field is numeric (should be right-aligned)
   */
  /**
   * Get validation error message for a field
   */
  getErrorMessage(field: any, rowControl: FormGroup): string {
    const fieldKey = this.getFieldKey(field.field_name);
    const control = rowControl.get(fieldKey);
    
    if (control && control.invalid && (control.dirty || control.touched)) {
      if (control.errors?.['required']) {
        return `${field.display_name || field.field_name} is required`;
      }
      if (control.errors?.['min']) {
        return `Value cannot be negative`;
      }
    }
    return '';
  }

  isNumericField(field: any): boolean {
    if (!field) return false;
    
    const dataType = (field.data_type || '').toLowerCase();
    const fieldType = (field.field_type || '').toLowerCase();
    
    // Check data_type
    if (dataType === 'number' || dataType === 'decimal' || dataType === 'float' || dataType === 'double' || dataType === 'currency') {
      return true;
    }
    
    // Check field_type
    if (fieldType === 'number') {
      return true;
    }
    
    // Check if field name suggests it's numeric
    const fieldName = (field.field_name || '').toLowerCase();
    if (fieldName.includes('amount') || fieldName.includes('price') || fieldName.includes('total') || 
        fieldName.includes('quantity') || fieldName.includes('qty') || fieldName.includes('count')) {
      return true;
    }
    
    return false;
  }

  makeKey(r: number, c: number): Key {
    return `${r}:${c}`;
  }

  cacheFilter(r: number, c: number) {
    const key = this.makeKey(r, c);
  
    setTimeout(() => {
      const overlay = document.querySelector('.p-select-overlay');
      if (!overlay) return;
  
      const options = overlay.querySelectorAll('.p-select-option:not(.p-disabled)');
      this.filteredMap.set(key, Array.from(options) as HTMLElement[]);
    });
  }

  onShow(r: number, c: number) {
    this.activeKey = this.makeKey(r, c);
  
    setTimeout(() => {
      const input = document.querySelector('.p-select-filter') as HTMLInputElement;
      if (!input) return;
  
      this.enterSub = fromEvent<KeyboardEvent>(input, 'keydown').subscribe(e => {
        if (e.key === 'Tab') this.tryAutoPick();
      });
    });
  }

  onHide(r: number, c: number) {
    if (this.activeKey === this.makeKey(r, c)) {
      this.enterSub?.unsubscribe();
      this.enterSub = undefined;
      this.activeKey = undefined;
    }
  }

  tryAutoPick() {
    if (!this.activeKey) return;
  
    const options = this.filteredMap.get(this.activeKey) || [];
    if (options.length === 1) {
      options[0].click();
    }
  }
  shouldFormatCurrency(col: any): boolean {
    
    if (!this.billInUsd) return false;
    const name = (col.field_name || '').toLowerCase();

    if (name.includes('tax_rate') ||name.includes('discount_rate') ||name.includes('interest_rate')) {
      return false;
    }

    return (
      col.data_type === 'currency' ||
      name.includes('amount') ||
      name.includes('price') ||
      name.includes('rate')
    );
  }
  getFormattedCellValue(col: any, rowData: any) {

    const control = rowData.control?.get(
      this.getFieldKey(col.field_name)
    );

    const value = control?.value;

    if (!this.shouldFormatCurrency(col)) {
      return this.formatDisplayValue(value, col, rowData.control, rowData.index);
    }

    const num = Number(value) || 0;

    return this.formatCurrencyWithUSD(num);
  }

  /** Returns the raw value of a field for a given row's FormGroup — used for status pill keying. */
  getStatusKey(col: any, rowControl: FormGroup): string {
    const v = rowControl?.get(this.getFieldKey(col.field_name))?.value;
    return v == null ? '' : String(v);
  }

  formatCurrencyWithUSD(amount: number): string {
    if (amount === null || amount === undefined) return '₹0.00';

    const inr = Number(amount);
    if (this.billInUsd && this.conversionRate) {
      const usd = inr / this.conversionRate;

      return `₹${inr.toLocaleString('en-IN', { minimumFractionDigits: 2 })} ($${usd.toLocaleString('en-US', { minimumFractionDigits: 2 })})`;
    }

    return `₹${inr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  }

  async applyLineOfBusinessLogic(row: FormGroup, value: any) {
    const lobDeptmap : Record<string,string> = {
      'PFS': 'Business Ops'
    };

    const dept = lobDeptmap[this.lineOfBusinessMap[value]]; // id => LOB name => associated dept

    if (dept) {
      row.get('department')?.setValue(dept, { emitEvent:false });
    }
  }

}
